import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Store, Package, Sparkles, Settings as SettingsIcon, LogOut, Menu, X, Gauge, Share2, FlaskConical, Plug, Map, Download, Fingerprint, Shield } from "lucide-react";import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthProvider";
import { StoreProvider } from "@/context/StoreProvider";
import Logo from "@/components/common/Logo";
import ThemeToggle from "@/components/common/ThemeToggle";
import LanguageToggle from "@/components/common/LanguageToggle";

function SidebarContent({ onNavigate, isAdmin }) {
  const t = useT();
  const items = [
    { to: "/dashboard", end: true, icon: LayoutDashboard, label: t("dashboard.sidebar.dashboard"), testid: "nav-dashboard" },
    { to: "/dashboard/store", icon: Store, label: t("dashboard.sidebar.myStore"), testid: "nav-store" },
    { to: "/dashboard/import", icon: Download, label: t("dashboard.sidebar.import"), testid: "nav-import", accent: true },
    { to: "/dashboard/shopify", icon: Plug, label: t("dashboard.sidebar.shopify"), testid: "nav-shopify", accent: true },
    { to: "/dashboard/products", icon: Package, label: t("dashboard.sidebar.products"), testid: "nav-products" },
    { to: "/dashboard/identity", icon: Fingerprint, label: t("dashboard.sidebar.identity"), testid: "nav-identity", accent: true },
    { to: "/dashboard/ai", icon: Sparkles, label: t("dashboard.sidebar.ai"), testid: "nav-ai", accent: true },
    { to: "/dashboard/readiness", icon: Gauge, label: t("dashboard.sidebar.readiness"), testid: "nav-readiness", accent: true },
    { to: "/dashboard/share", icon: Share2, label: t("dashboard.sidebar.share"), testid: "nav-share" },
    { to: "/dashboard/test", icon: FlaskConical, label: t("dashboard.sidebar.teststore"), testid: "nav-test" },
    { to: "/dashboard/connectors", icon: Plug, label: t("dashboard.sidebar.connectors"), testid: "nav-connectors" },
    { to: "/dashboard/roadmap", icon: Map, label: t("dashboard.sidebar.roadmap"), testid: "nav-roadmap" },
    { to: "/dashboard/settings", icon: SettingsIcon, label: t("dashboard.sidebar.settings"), testid: "nav-settings" },
  ];
  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={it.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <it.icon className={`h-4 w-4 ${it.accent && !isActive ? "text-brand" : ""}`} />
              {it.label}
            </>
          )}
        </NavLink>
      ))}
      {isAdmin && (
        <NavLink
          to="/admin"
          onClick={onNavigate}
          data-testid="nav-admin-console"
          className="mt-2 flex items-center gap-3 rounded-md border border-brand/40 bg-brand/5 px-3 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-brand/10"
        >
          <Shield className="h-4 w-4" />
          {t("dashboard.sidebar.admin")}
        </NavLink>
      )}
    </nav>
  );
}

export default function DashboardLayout() {
  const t = useT();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <StoreProvider>
      <div className="flex min-h-screen bg-background">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-border bg-card lg:flex">
          <div className="flex h-16 items-center border-b border-border px-5"><Logo to="/dashboard" /></div>
          <SidebarContent isAdmin={user?.is_admin} />
          <div className="border-t border-border p-3">
            <div className="mb-2 flex items-center gap-3 rounded-md px-3 py-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 font-mono text-sm font-bold text-brand">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{user?.name}</p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <button onClick={doLogout} data-testid="logout-button"
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground">
              <LogOut className="h-4 w-4" />{t("dashboard.logout")}
            </button>
          </div>
        </aside>

        {/* Mobile sidebar */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-border bg-card">
              <div className="flex h-16 items-center justify-between border-b border-border px-5">
                <Logo to="/dashboard" />
                <button onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></button>
              </div>
              <SidebarContent onNavigate={() => setMobileOpen(false)} isAdmin={user?.is_admin} />
              <div className="border-t border-border p-3">
                <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-secondary/60">
                  <LogOut className="h-4 w-4" />{t("dashboard.logout")}
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
          <header className="glass sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border px-6">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-btn"><Menu className="h-5 w-5" /></button>
            <div className="ml-auto flex items-center gap-2">
              <LanguageToggle />
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </StoreProvider>
  );
}
